import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap, filter, catchError } from 'rxjs/operators';

import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { COMPANY_STATUSES, REGEX_PHONE } from '../../constants';
import { ROLES_STATUS } from '../../enums';
import { AccountDTO, AccountFilesDTO, CompanyDTO } from '../../interfaces/models';
import { PhoneNumberPipe } from '../../pipes/number-phone';
import { AddressAutocompleteService } from '../../providers/address-autocomplete/address-autocomplete.service';
import { CompanyService } from '../../providers/company/company.service';
import { FileService } from '../../providers/file/file.service';
import { HereService, HereGeocodeResult } from '../../providers/here/here.service';
import { LoadingService } from '../../providers/loading/loading.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { UserService } from '../../providers/user/user.service';
import { NewAttachmentComponent } from '../new-attachments/new-attachments.component';

import { WalletDTO } from '../../interfaces/walletDTO';
import { CreateWalletComponent } from '../../companies/create-wallet/create-wallet.component';
import { CreateWalletRequest } from '../../companies/interfaces/createWalletRequest';
import { DirectivesModule } from '../../directives.module';

@Component({
  selector: 'app-my-company',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    DirectivesModule,
  ],
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss'],
})
export class MyCompanyComponent implements OnInit {
  private static readonly AUTOCOMPLETE_FLOW = 'company_profile';

  @ViewChild('document') document;
  @Input() companyWallet: WalletDTO;
  user: AccountDTO;
  company: CompanyDTO;
  editable = false;
  cover_photo: string;
  validatorAddress$ = new Subject<string>();
  companyStatuses = COMPANY_STATUSES;
  roles = ROLES_STATUS;
  readonly = true;
  displayAddressOption = (value: unknown) => this.addressAutocompleteService.getDisplayValue(value);

  constructor(
    private fb: FormBuilder,
    private loadingService: LoadingService,
    private companyService: CompanyService,
    private hereService: HereService,
    private fileService: FileService,
    private notificationService: NotificationService,
    private phoneNumberPipe: PhoneNumberPipe,
    private userService: UserService,
    private addressAutocompleteService: AddressAutocompleteService,
    public dialog: MatDialog,
  ) { }

  public data = this.fb.group({
    contactPersonPhone: new FormControl('', [Validators.pattern(REGEX_PHONE)]),
    address: new FormControl('', [Validators.required]),
    state: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    officePhone: new FormControl('', [Validators.required, Validators.pattern(REGEX_PHONE)]),
    zip: new FormControl('', [Validators.required]),
    blocked: new FormControl<boolean>(false, [Validators.required]),
  });

  public bankInfoForm = this.fb.group({
    accountNumber: new FormControl('', [Validators.required, Validators.minLength(1),
    Validators.maxLength(17)]),
    routingNumber: new FormControl('', [Validators.required, Validators.minLength(8),
    Validators.maxLength(9)]),
  });

  addressLocationOptions$ = this.data.get('address').valueChanges.pipe(
    map(value => this.addressAutocompleteService.getQueryValue(value)),
    map(query => this.addressAutocompleteService.normalizeQuery(query)),
    debounceTime(500),
    distinctUntilChanged(),
    tap((query: string) => this.addressAutocompleteService.trackTyped(MyCompanyComponent.AUTOCOMPLETE_FLOW, query)),
    filter(query => this.addressAutocompleteService.hasMinimumQueryLength(query)),
    switchMap((query: string) => this.hereService.getGeocode(query).pipe(
      tap((results: HereGeocodeResult[]) =>
        this.addressAutocompleteService.trackSuggestionsShown(
          MyCompanyComponent.AUTOCOMPLETE_FLOW,
          query,
          results.length,
        )),
      catchError(() => of([])),
    )),
  );

  ngOnInit() {
    this.loadingService.startLoading();
    this.user = this.userService.user;
    this.getCompanyWallet();
    this.getCompany();
    this.onChangePhone();
    this.validatorAddress$.pipe(
      debounceTime(600),
      distinctUntilChanged())
      .subscribe(() => {
        this.checkAddress();
      });
  }

  public editMode() {
    this.readonly = !this.readonly;
  }

  public addressValidator(address: string) {
    this.validatorAddress$.next(address);
  }

  public setAddress(addressObj: HereGeocodeResult) {
    const label = this.addressAutocompleteService.getDisplayValue(addressObj);

    this.data.patchValue(
      this.addressAutocompleteService.buildSelectionPatch(addressObj, {
        stateFormat: 'fullName',
        zipFieldName: 'zip',
      }),
    );
    this.addressAutocompleteService.trackSelected(MyCompanyComponent.AUTOCOMPLETE_FLOW, addressObj);
    this.validatorAddress$.next(label);
  }

  private checkAddress() {
    const { address, city, state, zip } = this.data.value;
    if (address || city || state || zip) {
      this.hereService.validateAddress({
        state,
        address,
        city,
        zipCode: zip,
      })
        .subscribe(
          () => {
            this.setErrors(null);
          },
          () => {
            this.setErrors({ notEquivalent: true });
          });
    }
  }

  private setErrors(err: ValidationErrors | null) {
    const { address, city, state, zip } = this.data.controls;
    address.setErrors(err);
    city.setErrors(err);
    state.setErrors(err);
    zip.setErrors(err);
  }

  private onChangePhone() {
    this.data.valueChanges.subscribe((val) => {
      const { contactPersonPhone, officePhone } = val;
      if (contactPersonPhone && contactPersonPhone.length) {
        const formatPhone = this.phoneNumberPipe.transform(contactPersonPhone);
        this.data.get('contactPersonPhone').patchValue(formatPhone, { emitEvent: false });
      }
      if (officePhone && officePhone.length) {
        const formatPhone = this.phoneNumberPipe.transform(officePhone);
        this.data.get('officePhone').patchValue(formatPhone, { emitEvent: false });
      }
    });
  }

  private getCompany() {
    this.companyService.get()
      .subscribe((res) => {
        this.company = res;
        this.cover_photo = this.company.avatarUrl;
        this.data.patchValue({ blocked: res.blocked });
        this.loadingService.stopLoading();
      });
  }

  private getCompanyWallet() {
    this.companyService.getWallet()
      .subscribe((res) => {
        this.companyWallet = res;
      });
  }

  public openAddWalletDialog() {
    const dialogRef = this.dialog.open(CreateWalletComponent, {
      width: '600px',
      height: '785px',
      data: {
        company: this.company, accountNumber: this.bankInfoForm.get('accountNumber').value,
        routingNumber: this.bankInfoForm.get('routingNumber').value
      },
    });

    dialogRef
      .afterClosed().pipe(filter(wallet => !!wallet),
        tap(() => this.loadingService.startLoading()),
        switchMap((wallet: CreateWalletRequest) => this.companyService.createWallet(wallet)),
        tap(() => this.loadingService.stopLoading()),
        tap(() => this.companyService.changeRequested.emit()),
      ).subscribe(() => {
        this.getCompanyWallet();
        this.openWalletAddedDialog();
      });
  }

  public openWalletAddedDialog() {
    const options = {
      width: '450px',
      data: {
        action: 'Confirm Block',
        description: 'Wallet set up successfully',
        nameButton: 'Confirm',
        hideCancel: true,
      },
    };

    this.dialog.open(ConfirmDialogComponent, options);
  }
  public deleteCompanyWallet() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        action: 'Confirm Delete',
        description: 'Are you sure?',
        nameButton: 'Confirm',
        reason: false,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.loadingService.startLoading();
      this.companyService.deleteWallet().subscribe((result) => {
        if (result) {
          this.companyWallet = null;
          this.loadingService.stopLoading();
          this.companyService.changeRequested.emit();
        }
      });
    });
  }

  public edit() {
    this.editable = !this.editable;
    if (this.editable) {
      Object.keys(this.company).forEach((key) => {
        if (this.data.controls[key] && this.company[key]) {
          this.data.controls[key].setValue(this.company[key]);
        }
      });
    }
  }

  private getValue(key) {
    return this.data.get(key).value;
  }

  public save() {
    this.loadingService.startLoading();
    this.addressAutocompleteService.trackSubmitted(MyCompanyComponent.AUTOCOMPLETE_FLOW);
    const data = {
      contactPersonPhone: this.getValue('contactPersonPhone'),
      address: this.getValue('address'),
      state: this.getValue('state'),
      city: this.getValue('city'),
      officePhone: this.getValue('officePhone'),
      zip: this.getValue('zip'),
      blocked: this.getValue('blocked'),
    };
    this.updateCompany(data);
  }

  private updateCompany(data = {}) {
    this.companyService.update(
      {
        ...this.company,
        ...data,
        avatarUrl: this.cover_photo,
      })
      .subscribe((res) => {
        this.editable = false;
        this.company = { ...this.company, ...res };
        this.loadingService.stopLoading();
      });
  }

  public uploadPhoto(file: File) {
    if (!file) {
      return;
    }
    this.loadingService.startLoading();
    this.fileService.upload(file)
      .subscribe((res) => {
        this.cover_photo = res.signedUrl;
        this.loadingService.stopLoading();
        this.notificationService.success('Successfully uploaded');
      });
  }

  public removeCover() {
    this.cover_photo = null;
  }

  public uploadDocument(file: File, type: string) {
    if (!file) {
      return;
    }
    this.loadingService.startLoading();
    this.fileService.upload(file)
      .subscribe((res) => {
        this.company[type] = res.signedUrl;
        this.loadingService.stopLoading();
        this.notificationService.success('Successfully uploaded');
      });
  }

  public checkApprove() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        action: 'Confirm Approve Company',
        description: 'Are you sure?',
        nameButton: 'Approve',
        reason: false,
      },
      width: '450px',
    });
    dialogRef.afterClosed()
      .subscribe((result) => {
        if (result) {
          this.approveCompany();
        }
      });
  }

  private approveCompany() {
    this.loadingService.startLoading();
    this.companyService.aproveCompany(this.company.id)
      .subscribe(
        () => {
          this.company.status = COMPANY_STATUSES.ACTIVE;
          this.loadingService.stopLoading();
          this.notificationService.success('Success approved!');
        },
        () => {
          this.loadingService.stopLoading();
        });
  }

  public requestChanges() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        action: 'Request Changes',
        description: '',
        nameButton: 'Send',
        reason: true,
        placeholder: 'Changes...',
        maxlength: 400,
      },
      width: '450px',
    });
    dialogRef.afterClosed()
      .subscribe((result) => {
        if (result) {
          this.companyService.requestChangesCompany({ id: this.company.id, message: result })
            .subscribe(() => {
              this.notificationService.success('Success send!');
            });
        }
      });
  }

  public downloadAttachment(file: AccountFilesDTO) {
    this.loadingService.startLoading();
    this.userService.getFileSign(file.path)
      .subscribe((resp) => {
        window.open(resp.url, '_parent');
        this.loadingService.stopLoading();
      });
  }

  public removeAttachment(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        action: 'Confirm Remove',
        description: 'Are you sure?',
        nameButton: 'Confirm',
        reason: false,
      },
    });

    dialogRef.afterClosed()
      .subscribe((result) => {
        if (result) {
          this.company.files = this.company.files.filter(file => file.id !== id);
          this.updateCompany({ files: this.company.files });
        }
      });
  }

  public uploadAttachments(file: File) {
    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '450px',
      data: {
        name: '',
      },
    });

    dialogRef.afterClosed()
      .subscribe((result) => {
        if (result) {
          this.createAttachment(file, result);
        }
      });
  }

  private createAttachment(file: File, displayName: string) {
    if (!file.name) {
      this.notificationService.error('Please select File');
      return;
    }
    this.loadingService.startLoading();
    this.document.nativeElement.value = null;
    this.fileService.upload(file)
      .subscribe(
        (res) => {
          this.company.files = [{
            displayName,
            path: res.url,
          }, ...this.company.files];
          this.updateCompany({ files: this.company.files });
        },
        () => {
          this.loadingService.stopLoading();
        });
  }
}
